/**
 * The GM tool catalogue (architecture §3.3).
 *
 * Fourteen strict schemas, in a fixed order sorted by name, exported into prompt
 * layer A and never changed inside a session (changing them invalidates the cache).
 * Field names and enum members here are the wire contract with the model and match
 * §3.3 exactly, which is why they are snake_case while the rest of the package is
 * camelCase.
 *
 * Two classes decide the turn-loop cost:
 *  - **pre-narration**: returns a result the model must read; costs one counted
 *    continuation request.
 *  - **trailing**: records a consequence, never needs a result, ends the response.
 *
 * There is no `roll_dice`, `apply_damage`, `apply_condition`, `set_hp`, `set_price`,
 * `reroll`, `fudge` or per-round enemy-intent tool — and no tool with which the model
 * or the director can *schedule* a fight.
 */
import { z } from 'zod';
import { CheckKey, Cover, DifficultyTier, Lighting, NpcMood, RumorCategory, Weather, } from './primitives.js';
/* ------------------------------------------------------------- tool class */
export const ToolClass = z.enum(['pre', 'trailing']);
/* --------------------------------------------------------- pre-narration */
export const RequestCheckInput = z
    .object({
    actor: z.string(),
    kind: z.enum(['skill', 'ability', 'save']),
    key: CheckKey,
    tier: DifficultyTier,
    advantage_hint: z.enum(['none', 'advantage', 'disadvantage']),
    /** ADR-091: what a save resists — a trait that grants advantage against it then applies. */
    against: z.enum(['poisoned', 'frightened', 'charmed']).optional(),
    stakes: z.string().max(160),
    consequence_on_fail: z.enum([
        'none',
        'condition:frightened',
        'condition:prone',
        'damage:minor',
        'damage:moderate',
        'alarm',
        'relationship:minor',
    ]),
})
    .strict();
export const StartCombatEnemy = z
    .object({
    stat_block: z.string(),
    count: z.number().int().min(1).max(12),
    disposition: z.enum(['hostile', 'defensive', 'fleeing']),
    intent: z.enum([
        'hold_chokepoint',
        'focus_weakest',
        'flank',
        'guard_npc',
        'flee_when_bloodied',
    ]),
})
    .strict();
export const ScenePatchProp = z
    .object({
    kind: z.string(),
    near: z.string(),
    cover: Cover,
})
    .strict();
export const ScenePatch = z
    .object({
    props_add: z.array(ScenePatchProp).max(5),
    lighting: Lighting,
    weather: Weather,
    variant: z.string(),
})
    .strict();
export const StartCombatInput = z
    .object({
    location_ref: z.string(),
    enemies: z.array(StartCombatEnemy).min(1).max(6),
    surprise: z.enum([
        'none',
        'player_surprises',
        'player_surprised',
        'contested_stealth',
    ]),
    objective: z.enum(['defeat', 'escape', 'protect', 'survive_rounds']),
    scene_patch: ScenePatch,
})
    .strict();
export const RecallInput = z
    .object({
    query: z.string().max(120),
    k: z.number().int().min(1).max(5),
})
    .strict();
export const SetSceneInput = z
    .object({
    location_ref: z.string(),
    /** Empty string means "no mood tag". */
    mood_tag: z.string().max(40),
})
    .strict();
/* ADR-088 made the player the only thing that moves the character, and ADR-107
 * removed the narrator's `travel` tool: the engine refused every call it could
 * make, unconditionally, so it could do nothing but spend a pre-narration
 * continuation and teach the model a rule that does not exist. The player's own
 * `travel` *command* is unaffected and keeps its schema in `commands.ts`. */
export const AdvanceTimeInput = z
    .object({
    unit: z.enum(['scene', 'period', 'short_rest', 'long_rest', 'days']),
    n: z.number().int().min(1).max(7),
})
    .strict();
export const TradeInput = z
    .object({
    npc: z.string(),
    item_id: z.string(),
    qty: z.number().int().min(1).max(99),
    side: z.enum(['buy', 'sell']),
})
    .strict();
/* -------------------------------------------------------------- trailing */
export const ApplySceneOutcomeInput = z
    .object({
    npc: z.string(),
    relationship_delta: z
        .object({
        trust: z.number().int().min(-20).max(20),
        respect: z.number().int().min(-20).max(20),
        affinity: z.number().int().min(-20).max(20),
        fear: z.number().int().min(-20).max(20),
    })
        .strict(),
    mood: NpcMood,
    /** Empty string means "no memory note". */
    memory_note: z.string().max(120),
    /** ADR-096: the Suggestion this NPC was under has been carried out; the spell ends for them. */
    suggestion_done: z.boolean().optional(),
    /**
     * Coin the exchange settled with no table open: a fee received, a price named
     * and paid, a toll or bribe. In copper (1 gp = 100 cp, 1 sp = 10 cp). Never for
     * coin the player already handed over this turn — the engine took that — and
     * never while a negotiation is open: a deal pays itself when it closes.
     */
    coins: z
        .object({
        direction: z.enum(['player_pays', 'player_receives']),
        amount_cp: z.number().int().min(1).max(1_000_000),
        reason: z.string().max(120),
    })
        .strict()
        .optional(),
    /** `category: 'none'` means "no rumor seeded". */
    rumor_seed: z
        .object({
        category: RumorCategory,
        about: z.string().max(80),
    })
        .strict(),
})
    .strict();
export const AssertFactInput = z
    .object({
    entity_ref: z.string(),
    fact: z.string().max(200),
    kind: z.enum(['appearance', 'backstory', 'belief', 'world']),
})
    .strict();
export const QuestAdvanceInput = z
    .object({
    quest_id: z.string(),
    to_stage: z.string(),
    evidence: z.string().max(160),
})
    .strict();
export const PromiseInput = z
    .object({
    kind: z.enum(['threat', 'reward', 'mystery', 'reunion']),
    text: z.string().max(160),
    payoff_by_session: z.number().int().min(1).max(3),
})
    .strict();
export const EmitCueInput = z
    .object({
    kind: z.enum(['sfx', 'music_stem', 'weather', 'lighting']),
    /** Validated at runtime against the client asset registry allow-list. */
    id: z.string().regex(/^[a-z0-9_.]+$/),
    intensity: z.number().int().min(1).max(3),
})
    .strict();
export const OfferChoicesInput = z
    .object({
    options: z.array(z.string().max(60)).min(2).max(4),
})
    .strict();
export const EndSessionInput = z
    .object({
    cliffhanger_hook_ref: z.string(),
    summary_hints: z.array(z.string().max(160)).max(5),
})
    .strict();
function tool(def) {
    return def;
}
const CATALOGUE = [
    tool({
        name: 'advance_time',
        toolClass: 'pre',
        description: 'Advance world time for a rest, a wait or a montage. Returns the notable events that happened. Call this before narrating the passage of time.',
        schema: AdvanceTimeInput,
        usuallyPreResolved: true,
    }),
    tool({
        name: 'apply_scene_outcome',
        toolClass: 'trailing',
        description: 'Record the social consequence of a dialogue turn on one NPC: relationship deltas, the mood they end the exchange in, an optional memory note and an optional rumor seed. Deltas are clamped by the engine.',
        schema: ApplySceneOutcomeInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'assert_fact',
        toolClass: 'trailing',
        description: 'Write a new canon fact onto an entity card. Use it for details you introduced in narration so the world remembers them. A fact that contradicts an authored or simulated fact is rejected and returns a correction.',
        schema: AssertFactInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'emit_cue',
        toolClass: 'trailing',
        description: 'Request an audio or visual cue (a sound effect, a music stem, weather, lighting). Ids are validated against the campaign asset registry.',
        schema: EmitCueInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'end_session',
        toolClass: 'trailing',
        description: 'Close the session on a cliffhanger. Refused before 100 minutes unless the player asked, and refused when no promise has been paid off and none was explicitly deferred.',
        schema: EndSessionInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'offer_choices',
        toolClass: 'trailing',
        description: 'Offer two to four short suggested actions as chips. They are suggestions only; free text is always available to the player.',
        schema: OfferChoicesInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'promise',
        toolClass: 'trailing',
        description: 'Register a narrative promise you have just made — a threat, a reward, a mystery or a reunion — with the session by which it should pay off.',
        schema: PromiseInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'quest_advance',
        toolClass: 'trailing',
        description: 'Move a quest to a new stage. You may only name a transition the turn listed as currently available; any other target is rejected.',
        schema: QuestAdvanceInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'recall',
        toolClass: 'pre',
        description: 'Retrieve a dossier on an entity who is not present, or on a past event. Returns cards with their provenance. Call this before asserting anything about someone you cannot see.',
        schema: RecallInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'request_check',
        toolClass: 'pre',
        description: 'Resolve an ability check, skill check or saving throw for an action whose outcome is uncertain and that the engine has not already resolved (committed events are listed in the turn). Call this BEFORE narrating; never state success or failure until the result returns.',
        schema: RequestCheckInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'set_scene',
        toolClass: 'pre',
        description: 'Dress the place the character is already standing in: pass its own location_ref and a mood tag, and the scene packet comes back with the light, the weather and the room as they now are. This does NOT move anyone — only the player moves the character, by saying so — and naming any other place is refused. When the player asks for somewhere they are not, do not call this: say where they are and what lies the way they asked.',
        schema: SetSceneInput,
        usuallyPreResolved: true,
    }),
    tool({
        name: 'start_combat',
        toolClass: 'pre',
        description: 'Start tactical combat in the current location. The board is built from the location’s map template and the people actually present; you may add at most five props. Call this before describing positions; do not narrate hits or damage — the engine returns the opening board.',
        schema: StartCombatInput,
        usuallyPreResolved: false,
    }),
    tool({
        name: 'trade',
        toolClass: 'pre',
        description: 'Register a buy or sell intent with an NPC. The engine prices the goods from the settlement market and the player confirms on the HUD. Never state a price yourself.',
        schema: TradeInput,
        usuallyPreResolved: true,
    }),
];
/** The catalogue in its fixed, name-sorted order — the order layer A renders. */
export const GM_TOOLS = Object.freeze([...CATALOGUE].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)));
export const GM_TOOL_NAMES = Object.freeze(GM_TOOLS.map((t) => t.name));
/** Tools that cost a counted continuation request when called (§3.3). */
export const PRE_NARRATION_TOOLS = Object.freeze(GM_TOOLS.filter((t) => t.toolClass === 'pre').map((t) => t.name));
/** Tools that record a consequence and end the response (§3.3). */
export const TRAILING_TOOLS = Object.freeze(GM_TOOLS.filter((t) => t.toolClass === 'trailing').map((t) => t.name));
export function getGmTool(name) {
    return GM_TOOLS.find((t) => t.name === name);
}
/** Average budget and hard cap on pre-narration continuations per session (§3.3). */
/**
 * §3.3's continuation budget. `perTurn` was added on 2026-09-05: a response that
 * ends in a pre-narration call buys a continuation, and a continuation that ends
 * in another may buy one more — a rejected `set_scene` followed by a corrected
 * one is the ordinary shape of the model learning a ref — but not a third, so a
 * model that keeps guessing cannot spend the session's cap inside one turn.
 */
export const PRE_NARRATION_BUDGET = Object.freeze({ average: 15, hardCap: 25, perTurn: 2 });
/* ------------------------------------------------------- tool call frames */
/** A tool call as the actor records it in the event log and replays it. */
export const GmToolCall = z.object({
    id: z.string(),
    name: z.string(),
    input: z.unknown(),
});
/** A `tool_result` block; `is_error` carries a `RulesError.hint` line. */
export const GmToolResult = z.object({
    tool_use_id: z.string(),
    is_error: z.boolean().default(false),
    content: z.string(),
});
//# sourceMappingURL=gm-tools.js.map